import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBroker, getBrokers } from '../lib/api';
import { SEO, buildBrokerReviewSchema, buildBreadcrumbSchema } from '../components/SEO';
import { ExternalLink, Check, X, ArrowRight, AlertTriangle, Info, ChevronDown, ChevronUp, Shield, Zap, BarChart3, Users, Globe, Wallet } from 'lucide-react';

const BROKER_FEATURES = {
  'ic-markets': {
    tagline: 'Broker Overview:',
    logo: 'https://customer-assets.emergentagent.com/job_forex-platform-guide/artifacts/1tyw2ej2_IC_Markets_logo_main.gif',
    highlights: ['Ultra-Low Latency and Fast Execution', 'Spreads Starting from 0.0 Pips', 'Access to 200+ Instruments'],
    stats: [
      { label: 'Active Traders', value: '180,000+' },
      { label: 'Daily Volume', value: '$18.9B+' },
      { label: 'Instruments', value: '200+' },
      { label: 'Countries', value: '200+' },
    ],
    faqs: [
      { q: 'What platforms does IC Markets offer?', a: 'IC Markets offers MetaTrader 4, MetaTrader 5, and cTrader. Each platform supports advanced charting, algorithmic trading via Expert Advisors (EAs), and mobile trading on iOS and Android devices.' },
      { q: 'What is the minimum deposit for IC Markets?', a: 'The minimum deposit for IC Markets is $200 for all account types. This applies to Standard, Raw Spread, and cTrader Raw accounts.' },
      { q: 'Is IC Markets regulated?', a: 'Yes, IC Markets is regulated by the Australian Securities and Investments Commission (ASIC), the Cyprus Securities and Exchange Commission (CySEC), and the Financial Services Authority (FSA) of Seychelles.' },
      { q: 'Does IC Markets charge inactivity fees?', a: 'No, IC Markets does not charge inactivity fees. Your account remains active regardless of trading frequency.' },
    ]
  },
  'vantage-markets': {
    tagline: 'Broker Overview:',
    highlights: ['Multi-Regulated (ASIC, FCA, CIMA)', 'RAW ECN Spreads from 0.0 Pips', 'Copy Trading & Social Features Available'],
    stats: [
      { label: 'Countries Served', value: '172+' },
      { label: 'Platforms', value: '4' },
      { label: 'Instruments', value: '1,000+' },
      { label: 'Min. Deposit', value: '$50' },
    ],
    faqs: [
      { q: 'What platforms does Vantage Markets offer?', a: 'Vantage offers MetaTrader 4, MetaTrader 5, ProTrader, and the Vantage App. The ProTrader platform includes advanced charting powered by TradingView.' },
      { q: 'What is the minimum deposit for Vantage?', a: 'The minimum deposit is $50 for Standard STP accounts. RAW ECN accounts also start from $50, while Pro ECN accounts require a higher minimum.' },
      { q: 'Is Vantage Markets regulated?', a: 'Yes, Vantage is regulated by ASIC (Australia), FCA (UK), and CIMA (Cayman Islands), providing multi-jurisdictional regulatory oversight.' },
      { q: 'Does Vantage offer copy trading?', a: 'Yes, Vantage provides copy trading and social trading features, allowing you to follow and replicate the strategies of experienced traders.' },
    ]
  },
  'xm': {
    tagline: 'Broker Overview:',
    highlights: ['$5 Minimum Deposit', 'Regulated by CySEC, ASIC, DFSA', 'Extensive Education Program'],
    stats: [
      { label: 'Clients', value: '10M+' },
      { label: 'Countries', value: '190+' },
      { label: 'Languages', value: '30+' },
      { label: 'Min. Deposit', value: '$5' },
    ],
    faqs: [
      { q: 'What makes XM different from other brokers?', a: 'XM offers an extremely low $5 minimum deposit, a comprehensive educational program including daily webinars in multiple languages, and regulation by multiple tier-1 authorities including CySEC, ASIC, and DFSA.' },
      { q: 'Does XM charge inactivity fees?', a: 'Yes, XM charges a $5/month inactivity fee after 90 days of no trading activity. Accounts dormant for over 12 months may incur additional fees.' },
      { q: 'What account types does XM offer?', a: 'XM offers Micro, Standard, Ultra Low, and Shares accounts. Each is designed for different trading styles, from small-volume beginners to active traders.' },
      { q: 'Is XM suitable for beginners?', a: 'XM is considered accessible for newer traders due to its low minimum deposit, extensive educational resources, demo accounts, and responsive customer support in 30+ languages.' },
    ]
  },
  'roboforex': {
    tagline: 'Broker Overview:',
    logo: 'https://customer-assets.emergentagent.com/job_forex-platform-guide/artifacts/6opkbfk5_logo-bz.svg',
    highlights: ['Over 12,000 Instruments on R StocksTrader', 'Multiple Platform Options', 'Accounts from $10 Minimum Deposit'],
    companyInfo: 'RoboForex Ltd is a financial brokerage company regulated by the Financial Services Commission (FSC) of Belize. Source: RoboForex official website.',
    stats: [
      { label: 'Instruments', value: '12,000+' },
      { label: 'Countries', value: '169+' },
      { label: 'Platforms', value: '4' },
      { label: 'Min. Deposit', value: '$10' },
    ],
    faqs: [
      { q: 'What is R StocksTrader?', a: 'R StocksTrader is RoboForex\'s proprietary trading platform offering access to over 12,000 instruments including stocks, indices, ETFs, commodities, and forex. It features advanced charting, watchlists, and a modern interface.' },
      { q: 'What regulation does RoboForex have?', a: 'RoboForex Ltd is regulated by the Financial Services Commission (FSC) of Belize. Note that this is a tier-3 regulator, which offers a different level of oversight compared to ASIC or FCA.' },
      { q: 'What account types are available?', a: 'RoboForex offers Pro, ECN, R StocksTrader, ProCent (for beginners), and Prime accounts. Each caters to different trading styles and experience levels.' },
      { q: 'Does RoboForex offer a free VPS?', a: 'Yes, RoboForex provides a free VPS (Virtual Private Server) for active traders, which helps ensure uninterrupted automated trading with low latency.' },
    ]
  },
  'pu-prime': {
    tagline: 'Broker Overview:',
    highlights: ['ASIC & Multi-Jurisdiction Regulated', 'ECN Spreads from 0.0 Pips', 'Copy Trading Available'],
    stats: [
      { label: 'Founded', value: '2016' },
      { label: 'Platforms', value: '3' },
      { label: 'Asset Classes', value: '7+' },
      { label: 'Min. Deposit', value: '$20' },
    ],
    faqs: [
      { q: 'Is PU Prime regulated?', a: 'Yes, PU Prime is regulated by the Australian Securities and Investments Commission (ASIC), the Financial Services Authority (FSA) of Seychelles, and the Cayman Islands Monetary Authority (CIMA).' },
      { q: 'What is the minimum deposit?', a: 'The minimum deposit for PU Prime is $20 for standard accounts, making it accessible for traders with smaller capital.' },
      { q: 'Does PU Prime offer copy trading?', a: 'Yes, PU Prime provides copy trading features that allow you to follow and replicate trades from experienced traders on the platform.' },
      { q: 'What platforms does PU Prime support?', a: 'PU Prime supports MetaTrader 4, MetaTrader 5, and cTrader, offering a range of options for different trading styles and preferences.' },
    ]
  },
};

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-slate-900 pr-4">{question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1">
          <p className="text-sm text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function BrokerPage() {
  const { slug } = useParams();
  const [broker, setBroker] = useState(null);
  const [allBrokers, setAllBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [brokerRes, brokersRes] = await Promise.all([getBroker(slug), getBrokers()]);
        setBroker(brokerRes.data);
        setAllBrokers(brokersRes.data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  }

  if (!broker) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-slate-500">Broker not found</p>
        <Link to="/" className="text-blue-600 hover:text-blue-700 text-sm font-medium">Return to Homepage</Link>
      </div>
    );
  }

  const extra = BROKER_FEATURES[broker.slug] || BROKER_FEATURES['ic-markets'];
  const otherBrokers = allBrokers.filter(b => b.slug !== broker.slug);

  return (
    <div data-testid="broker-page">
      <SEO
        title={`${broker.name} Review ${new Date().getFullYear()}`}
        description={broker.short_description}
        canonical={`/${broker.slug}`}
        type="article"
        jsonLd={[
          buildBrokerReviewSchema(broker),
          buildBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Brokers', url: '/compare' },
            { name: broker.name }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 sm:py-20" data-testid="broker-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-xs text-slate-400 hover:text-slate-300 mb-6 transition-colors">
            Home <span className="mx-1.5">/</span> Brokers <span className="mx-1.5">/</span> <span className="text-slate-200">{broker.name}</span>
          </Link>
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1">
              <p className="text-sm text-slate-400 mb-2 font-medium">{extra.tagline}</p>
              {extra.logo && (
                <div className="mb-5">
                  <img src={extra.logo} alt={`${broker.name} logo`} className="h-14 w-auto object-contain" />
                  <p className="text-[10px] text-slate-500 mt-1">Source: {broker.name} official website.</p>
                </div>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold tracking-tight font-['Outfit'] mb-6">
                {broker.name}
              </h1>
              <div className="space-y-2.5 mb-8">
                {extra.highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-5 h-5 text-green-400 shrink-0" />
                    <span className="text-base text-slate-200">{h}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={broker.affiliate_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-12 px-8 transition-colors"
                  data-testid="broker-visit-btn"
                >
                  Visit {broker.name} <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/compare"
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-200 hover:bg-slate-800 h-12 px-6 transition-colors"
                  data-testid="broker-compare-btn"
                >
                  Compare Brokers
                </Link>
              </div>
              <p className="text-[10px] text-slate-500 mt-3">
                This link may be an affiliate link. We may receive compensation if you register through it.
              </p>
              <p className="text-[10px] text-red-400 mt-1">
                Trading leveraged financial instruments involves a high level of risk and may not be suitable for all investors.
              </p>
            </div>

            {/* Score Box */}
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 w-full lg:w-80 shrink-0" data-testid="broker-score-box">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-5">Quick Overview</h3>
              <div className="space-y-3.5">
                {[
                  { label: 'Regulation', value: broker.regulation },
                  { label: 'Min. Deposit', value: broker.min_deposit },
                  { label: 'Platforms', value: broker.platforms },
                  { label: 'Founded', value: broker.founded },
                  { label: 'Headquarters', value: broker.headquarters },
                ].map(item => (
                  <div key={item.label} className="flex justify-between text-sm">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-white font-medium text-right max-w-[55%]">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Disclosure Banner (kept) */}
      <div className="w-full bg-slate-50 border-b border-slate-200 px-4 py-2.5" data-testid="top-affiliate-disclaimer">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] text-slate-500 leading-relaxed text-center">
            <strong className="text-slate-600">Disclosure:</strong> This page reviews and compares several brokerage platforms including {broker.name}. MarketsReview.org is an independent website operated by VIP Affiliate Network Ltd{broker.slug === 'roboforex' ? ', a RoboForex partner' : ''}. We are not employees, representatives, or an affiliated company of {broker.slug === 'roboforex' ? 'RoboForex Ltd or ' : ''}any broker listed on this site. We may receive compensation from listed brokers through affiliate partnerships. All information is for educational and informational purposes only and does not constitute financial or investment advice.
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <section className="bg-white border-b border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {extra.stats.map(stat => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-slate-900 font-['Outfit']">{stat.value}</div>
                <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-14" data-testid="broker-about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">
            About {broker.name}
          </h2>
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              {broker.full_description}
            </p>
            <p className="text-[10px] text-slate-400 mb-6">Source: {broker.name} official website.</p>

            {/* RoboForex Company Info Block */}
            {extra.companyInfo && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6" data-testid="broker-company-info">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Official Company Information</p>
                <p className="text-xs text-slate-600 leading-relaxed">{extra.companyInfo}</p>
              </div>
            )}

            {/* Logo CTA Banner */}
            {extra.logo ? (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col sm:flex-row items-center gap-6">
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-center shrink-0">
                  <img src={extra.logo} alt={`${broker.name} logo`} className="h-12 w-auto object-contain max-w-[180px]" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-sm font-medium text-slate-900 mb-1">Explore {broker.name} Trading Platform</p>
                  <p className="text-xs text-slate-500">Visit the official {broker.name} website to learn more about their trading conditions and account options.</p>
                </div>
                <a
                  href={broker.affiliate_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-11 px-8 transition-colors shrink-0"
                  data-testid="broker-about-cta"
                >
                  Visit {broker.name} <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <a
                href={broker.affiliate_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-11 px-6 transition-colors"
                data-testid="broker-about-cta"
              >
                Visit {broker.name} Website <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <p className="text-[10px] text-slate-400 mt-2">This link may be an affiliate link. We may receive compensation if you register through it.</p>
            <p className="text-[10px] text-red-600 mt-1">Trading leveraged financial instruments involves a high level of risk and may not be suitable for all investors. You may lose more than your initial investment.</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Broker Snapshot Grid */}
        <section className="mb-14" data-testid="broker-snapshot">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">Broker Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Shield, label: 'Regulation', value: broker.regulation },
              { icon: Wallet, label: 'Min. Deposit', value: broker.min_deposit },
              { icon: BarChart3, label: 'Platforms', value: broker.platforms },
              { icon: Globe, label: 'Instruments', value: broker.instruments },
              { icon: Zap, label: 'Account Types', value: broker.account_types },
              { icon: Users, label: 'Customer Support', value: broker.customer_support },
            ].map(item => (
              <div key={item.label} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-3">
                <item.icon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{item.label}</span>
                  <p className="text-sm font-medium text-slate-900 mt-0.5">{item.value || 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pros & Cons */}
        <section className="mb-14" data-testid="pros-cons-section">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">Pros & Cons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50/50 border border-green-200 rounded-xl p-6">
              <h3 className="text-base font-semibold text-green-800 mb-4 flex items-center gap-2">
                <Check className="w-5 h-5" /> Advantages
              </h3>
              <ul className="space-y-3">
                {broker.pros.map((pro, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50/50 border border-red-200 rounded-xl p-6">
              <h3 className="text-base font-semibold text-red-800 mb-4 flex items-center gap-2">
                <X className="w-5 h-5" /> Disadvantages
              </h3>
              <ul className="space-y-3">
                {broker.cons.map((con, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <X className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Fees */}
        <section className="mb-14" data-testid="fees-section">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">Fees & Costs</h2>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <tbody>
                {[
                  { label: 'Spreads', value: broker.fees.spreads },
                  { label: 'Commission', value: broker.fees.commission },
                  { label: 'Swap / Overnight Fees', value: broker.fees.swap_fees },
                  { label: 'Inactivity Fee', value: broker.fees.inactivity_fee },
                ].map((fee, i) => (
                  <tr key={fee.label} className={`${i < 3 ? 'border-b border-slate-200' : ''}`}>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900 bg-slate-50 w-1/3">{fee.label}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{fee.value || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 text-center">
            <a
              href={broker.affiliate_url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-11 px-8 transition-colors"
              data-testid="fees-cta-btn"
            >
              Visit {broker.name} Website <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-[10px] text-slate-400 mt-2">Affiliate link. We may receive compensation if you register.</p>
            <p className="text-[10px] text-red-600 mt-1">Trading leveraged financial instruments involves a high level of risk and may not be suitable for all investors.</p>
          </div>
        </section>

        {/* Detailed Comparison Section */}
        <section className="mb-14" data-testid="competitor-comparison">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-3">
            How Does {broker.name} Compare?
          </h2>
          <p className="text-base text-slate-600 mb-6">Side-by-side comparison of {broker.name} with other reviewed brokers across key features, fees, and trading conditions.</p>

          <div className="w-full overflow-auto rounded-lg border border-slate-200 mb-4">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider min-w-[140px]">Feature</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-700 uppercase tracking-wider min-w-[150px] bg-blue-50/50">{broker.name}</th>
                  {otherBrokers.map(b => (
                    <th key={b.slug} className="px-4 py-3 text-center text-xs font-semibold text-slate-900 uppercase tracking-wider min-w-[150px]">{b.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Regulation', key: 'regulation' },
                  { label: 'Min. Deposit', key: 'min_deposit' },
                  { label: 'Platforms', key: 'platforms' },
                  { label: 'Spreads', render: (b) => b.fees?.spreads || 'N/A' },
                  { label: 'Commission', render: (b) => b.fees?.commission || 'N/A' },
                  { label: 'Instruments', key: 'instruments' },
                  { label: 'Account Types', key: 'account_types' },
                  { label: 'Max Leverage', key: 'leverage' },
                  { label: 'Deposit Methods', key: 'deposit_methods' },
                  { label: 'Customer Support', key: 'customer_support' },
                ].map((row) => (
                  <tr key={row.label} className="border-b border-slate-200 last:border-0">
                    <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-slate-900 border-r border-slate-100">{row.label}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-center bg-blue-50/20">{row.render ? row.render(broker) : (broker[row.key] || 'N/A')}</td>
                    {otherBrokers.map(b => (
                      <td key={b.slug} className="px-4 py-3 text-sm text-slate-700 text-center">{row.render ? row.render(b) : (b[row.key] || 'N/A')}</td>
                    ))}
                  </tr>
                ))}
                <tr className="border-b border-slate-200">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-green-700 border-r border-slate-100">Key Advantages</td>
                  <td className="px-4 py-3 align-top bg-blue-50/20">
                    <ul className="space-y-1.5">{broker.pros.slice(0, 3).map((p, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-green-600 shrink-0 mt-0.5" />{p}</li>
                    ))}</ul>
                  </td>
                  {otherBrokers.map(b => (
                    <td key={b.slug} className="px-4 py-3 align-top">
                      <ul className="space-y-1.5">{b.pros.slice(0, 3).map((p, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-green-600 shrink-0 mt-0.5" />{p}</li>
                      ))}</ul>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-red-700 border-r border-slate-100">Key Disadvantages</td>
                  <td className="px-4 py-3 align-top bg-blue-50/20">
                    <ul className="space-y-1.5">{broker.cons.slice(0, 2).map((c, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600"><X className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />{c}</li>
                    ))}</ul>
                  </td>
                  {otherBrokers.map(b => (
                    <td key={b.slug} className="px-4 py-3 align-top">
                      <ul className="space-y-1.5">{b.cons.slice(0, 2).map((c, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600"><X className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />{c}</li>
                      ))}</ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="sticky left-0 z-10 bg-white px-4 py-4 text-sm font-medium text-slate-900 border-r border-slate-100">Actions</td>
                  <td className="px-4 py-4 text-center bg-blue-50/20">
                    <a href={broker.affiliate_url} target="_blank" rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 h-8 px-4 transition-colors"
                      data-testid="comparison-cta-current">
                      Visit <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  {otherBrokers.map(b => (
                    <td key={b.slug} className="px-4 py-4 text-center">
                      <div className="flex flex-col gap-1.5 items-center">
                        <Link to={`/${b.slug}`} className="inline-flex items-center rounded-md text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 h-8 px-4 transition-colors">Review</Link>
                        <a href={b.affiliate_url} target="_blank" rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 h-8 px-4 transition-colors"
                          data-testid={`comparison-visit-${b.slug}`}>
                          Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 text-center">
            Broker links may be affiliate links. We may receive compensation if you register through them. This comparison is for informational purposes only. Source: respective brokers' official websites.
          </p>
        </section>

        {/* Assessment Summary */}
        <section className="mb-14" data-testid="verdict-section">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">Assessment Summary</h2>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Independent Assessment</span>
            </div>
            <p className="text-base text-slate-700 leading-relaxed">{broker.verdict}</p>
            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                This assessment is based on our independent review methodology and does not constitute financial advice.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-14" data-testid="faq-section">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {extra.faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-12" data-testid="broker-cta-section">
          <div className="bg-slate-900 text-white rounded-2xl p-8 sm:p-10 text-center">
            {extra.logo && (
              <div className="flex justify-center mb-6">
                <div className="bg-white rounded-xl p-4 inline-flex">
                  <img src={extra.logo} alt={`${broker.name} logo`} className="h-10 w-auto object-contain max-w-[160px]" />
                </div>
              </div>
            )}
            <h3 className="text-2xl font-bold font-['Outfit'] mb-3">Interested in {broker.name}?</h3>
            <p className="text-sm text-slate-300 mb-6 max-w-lg mx-auto">
              Visit the official {broker.name} website to learn more about their trading conditions, platform features, and account options. Risk applies to all trading activities.
            </p>
            <a
              href={broker.affiliate_url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-12 px-8 transition-colors"
              data-testid="broker-bottom-cta"
            >
              Visit {broker.name} Website <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-[10px] text-slate-500 mt-3">
              This link may be an affiliate link. We may receive compensation if you register through it.
            </p>
          </div>
        </section>
      </div>

      {/* Bottom disclaimers removed - all legal content is in the global footer */}
    </div>
  );
}
